import { useEffect, useRef, useState } from 'react';
import '@styles/style.css';
import { ConfigProvider as AntdConfigProvider, message } from "antd";
import ruRU from "antd/lib/locale/ru_RU";
import OrderTab from './OrderTab';
import CompanyTab from './companyTab/CompanyTab';
import {
  kcLogin,
  kcLogout,
  kcExtractUsernameFromTokens,
  kcGetAccessTokenFromOfflineToken
} from '@lib/keycloak';
import { bxGetCurrentMemberId, bxLoadOfflineToken } from '@lib/bx24';
import type { KeycloakTokens } from '../types/globals';
import { AuthContext } from './authContext';
import { Comments } from './comments/Comments';
import BankOrderPage from './bankOrder/BankOrderPage';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from './store/store';
import { Button } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import CustomerPage from './companyTab/CustomerPage';
import ProductBgPage from './productBg/ProductBgPage';
import "antd/dist/antd.variable.min.css";
import { updateMemberId } from '@services/memberApi';

export default function App() {
  const [ready, setReady] = useState<boolean>(false);
  const [auth, setAuth] = useState<KeycloakTokens | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPortalAdmin, setIsPortalAdmin] = useState<boolean>(false);
  const [hasOfflineToken, setHasOfflineToken] = useState<boolean>(false);
  const [placement, setPlacement] = useState<string | null>(null);
  const [placementOptions, setPlacementOptions] = useState<any>(null);

  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    try { setIsPortalAdmin(!!BX24.isAdmin()); } catch { }
    try { BX24.resizeWindow(document.body.scrollWidth, 890) } catch { }

    (async () => {
      try {
        const offlineToken = await bxLoadOfflineToken();
        setHasOfflineToken(!!offlineToken);
        if (offlineToken) {
          try {
            const tokens = await kcGetAccessTokenFromOfflineToken(offlineToken);
            setAuth(tokens);
            scheduleTokenRefresh(tokens);
            var info = BX24.placement.info();
            setPlacement(info.placement);
            setPlacementOptions(info.options);
          } catch {
            setErrorMessage('Не удалось обновить токен доступа. Пройдите авторизацию ещё раз');
          }
        }
      } catch (e: any) {
        setErrorMessage('Ошибка доступа к настройкам приложения: ' + (e?.message || e));
      }
      setReady(true);
    })();
  }, []);

  function clearTokenRefreshTimer() {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }

  function scheduleTokenRefresh(tokens: KeycloakTokens) {
    clearTokenRefreshTimer();
    const t = Math.max(10_000, ((tokens.expires_in ?? 60) - 30) * 1000);
    refreshTimer.current = setTimeout(async () => {
      try {
        const offlineToken = await bxLoadOfflineToken();
        setHasOfflineToken(!!offlineToken);
        if (!offlineToken) throw new Error('Нет сохранённого offline_access токена');
        const fresh = await kcGetAccessTokenFromOfflineToken(offlineToken);
        setAuth(fresh);
        scheduleTokenRefresh(fresh);
      } catch {
        setAuth(null);
        setErrorMessage('Требуется авторизация');
      }
    }, t);
  }

  async function handleAuthorize() {
    if (!isPortalAdmin) return;
    try {
      const tokens = await kcLogin();
      const memberId = bxGetCurrentMemberId();
      try {
        await updateMemberId(memberId, tokens.access_token);
      } catch (e) {
        message.warning("Не удалось зарегистрироваться для получения уведомлений");
        console.error("Failed to save memberId. Notifications won't work", e)
      }
      setAuth(tokens);
      setHasOfflineToken(true);
      setErrorMessage(null);
      scheduleTokenRefresh(tokens);
    } catch (e: any) {
      setErrorMessage(e?.message || String(e));
    }
  }

  async function handleLogout() {
    if (!isPortalAdmin) return;
    await kcLogout();
    setAuth(null);
    setHasOfflineToken(false);
  }

  const loginName = auth ? kcExtractUsernameFromTokens(auth) : '';
  const connected = !!auth?.access_token;

  useEffect(() => {
    localStorage.setItem("JWT", auth?.access_token ?? '');
  }, [auth]);

  return (
    <AntdConfigProvider locale={ruRU}>
      <AuthContext.Provider value={{ auth }}>
        <ReduxProvider store={store}>
          <div className="card">
            {!ready && <div className="row">Инициализация...</div>}
            {ready && (!placement || (placement === 'DEFAULT' && !placementOptions?.action)) && (
              <div className="topbar">
                <div className="left">
                  <span className={`badge ${connected ? '' : 'off'}`}>
                    <span className={`dot ${connected ? 'ok' : 'off'}`}></span>
                    {connected ? 'Подключено' : 'Не подключено'}
                  </span>
                  <span>FZ Connect</span>
                </div>
                <div className="right">
                  {connected && <div className="username" title={loginName}>Логин: {loginName}</div>}
                  {isPortalAdmin && !connected && <Button onClick={handleAuthorize}>Авторизоваться</Button>}
                  {isPortalAdmin && hasOfflineToken && <Button icon={<LogoutOutlined />} onClick={handleLogout}>Выйти</Button>}
                </div>
              </div>
            )}
            {ready && !connected && !isPortalAdmin && (<div className="row">Необходимо авторизоваться. Обратитесь к администратору</div>)}
            {errorMessage && (<div className="row err">{errorMessage}</div>)}
            {ready && connected && (
              <>
                {errorMessage && <div className="row err">{errorMessage}</div>}
                {placement === 'CRM_DEAL_DETAIL_TAB' && (
                  <OrderTab />
                )}
                {placement === 'CRM_COMPANY_DETAIL_TAB' && (
                  <CompanyTab />
                )}
                {placement === 'DEFAULT' && placementOptions?.action === 'comments' && (
                  <Comments comments={placementOptions.data} />
                )}
                {placement === 'DEFAULT' && placementOptions?.action === 'bankOrder' && (
                  <BankOrderPage orderId={placementOptions.data?.orderId} />
                )}
                {placement === 'DEFAULT' && placementOptions?.action === 'customer' && (
                  <CustomerPage customerId={placementOptions.data?.customerId} />
                )}
                {placement === 'DEFAULT' && placementOptions?.action === 'productBg' && (
                  <ProductBgPage productId={placementOptions.data?.productId} />
                )}
              </>
            )}
          </div>
        </ReduxProvider>
      </AuthContext.Provider>
    </AntdConfigProvider>
  );
}
